import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { AlertService } from '@cde-fe-app/app/cde-toast/services/alert.service';
import { ToasterClass, ToasterPosition, ToasterType } from '@cde-fe-app/app/cde-toast/utils/toastr-options';
import { IAginAuthorizationRequest } from '@cde-fe-app/app/shared/models/agin.model';
import { aginActions } from './agin.actions';
import { selectAginPendingConnectionData, selectAginPlatformsWithConnections } from './agin.selectors';
import { AginConnectionService } from '../../services/agin-connection.service';

@Injectable()
export class AginEffects {
  readonly alertService = inject(AlertService);
  readonly translateService = inject(TranslateService);
  readonly aginConnectionService = inject(AginConnectionService);
  readonly actions$ = inject(Actions);
  readonly store = inject(Store);

  loadScopes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(aginActions.loadAginScopes),
      mergeMap(() =>
        this.aginConnectionService.getAginScopes().pipe(
          map(scopes => aginActions.loadAginScopesSuccess({ scopes })),
          catchError(error => {
            this.alertService.showError(
              this.translateService.instant('couplings.notifications.load_connections.failure'),
              this.translateService.instant('alert_message.try_again'),
              ToasterType.snackbarError,
              {
                positionClass: ToasterPosition.bottomCenter,
                showToastAction: false,
                type: ToasterType.snackbarError,
                toastClass: ToasterClass.snackbarError,
                closeButton: true,
                disableTimeOut: false,
              }
            );
            return of(aginActions.loadAginScopesFailure({ error }));
          })
        )
      )
    )
  );

  loadPlatformsWithConnections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(aginActions.loadAginPlatformsWithConnections),
      mergeMap(() =>
        this.aginConnectionService.getAginPlatformsWithConnections().pipe(
          map(platforms => aginActions.loadAginPlatformsWithConnectionsSuccess({ platforms })),
          catchError(error => {
            this.alertService.showError(
              this.translateService.instant('couplings.notifications.load_connections.failure'),
              this.translateService.instant('alert_message.try_again'),
              ToasterType.snackbarError,
              {
                positionClass: ToasterPosition.bottomCenter,
                showToastAction: false,
                type: ToasterType.snackbarError,
                toastClass: ToasterClass.snackbarError,
                closeButton: true,
                disableTimeOut: false,
              }
            );
            return of(aginActions.loadAginPlatformsWithConnectionsFailure({ error }));
          })
        )
      )
    )
  );

  loadConnections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(aginActions.loadAginConnections),
      mergeMap(({ page, size }) =>
        this.aginConnectionService.getAginConnections(page, size).pipe(
          map(connections => aginActions.loadAginConnectionsSuccess({ connections })),
          catchError(error => {
            this.alertService.showError(
              this.translateService.instant('couplings.notifications.load_connections.failure'),
              this.translateService.instant('alert_message.try_again'),
              ToasterType.snackbarError,
              {
                positionClass: ToasterPosition.bottomCenter,
                showToastAction: false,
                type: ToasterType.snackbarError,
                toastClass: ToasterClass.snackbarError,
                closeButton: true,
                disableTimeOut: false,
              }
            );
            return of(aginActions.loadAginConnectionsFailure({ error }));
          })
        )
      )
    )
  );

  createConnection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(aginActions.createAginConnection),
      mergeMap(({ connectionRequest }) =>
        this.aginConnectionService
          .createAginConnection(connectionRequest.platformRequesting, connectionRequest.platformResponding)
          .pipe(
            map(response =>
              aginActions.createAginConnectionSuccess({ connectionRequestId: response.connectionRequestId })
            ),
            catchError(error => {
              this.alertService.showError(
                this.translateService.instant('couplings.dataconnect.inbound.notifications.create.failure'),
                this.translateService.instant('alert_message.try_again'),
                ToasterType.snackbarError,
                {
                  positionClass: ToasterPosition.bottomCenter,
                  showToastAction: false,
                  type: ToasterType.snackbarError,
                  toastClass: ToasterClass.snackbarError,
                  closeButton: true,
                  disableTimeOut: false,
                }
              );
              return of(aginActions.createAginConnectionFailure({ error }));
            })
          )
      )
    )
  );

  deleteConnection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(aginActions.deleteAginConnection),
      mergeMap(({ connectionId }) =>
        this.aginConnectionService.deleteAginConnection(connectionId).pipe(
          map(() => {
            this.alertService.callSnackbarSuccess(
              this.translateService.instant('couplings.dataconnect.inbound.notifications.delete.success'),
              '',
              ToasterType.snackbarSuccess,
              {
                positionClass: ToasterPosition.bottomCenter,
                showToastAction: false,
                type: ToasterType.snackbarSuccess,
                toastClass: ToasterClass.snackbarSuccess,
                closeButton: true,
                disableTimeOut: false,
              }
            );
            return aginActions.deleteAginConnectionSuccess({ connectionId });
          }),
          catchError(error => {
            this.alertService.showError(
              this.translateService.instant('couplings.dataconnect.inbound.notifications.delete.failure'),
              this.translateService.instant('alert_message.try_again'),
              ToasterType.snackbarError,
              {
                positionClass: ToasterPosition.bottomCenter,
                showToastAction: false,
                type: ToasterType.snackbarError,
                toastClass: ToasterClass.snackbarError,
                closeButton: true,
                disableTimeOut: false,
              }
            );
            return of(aginActions.deleteAginConnectionFailure({ error }));
          })
        )
      )
    )
  );

  getAuthorization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(aginActions.getAginAuthorization),
      mergeMap(({ request }) =>
        this.aginConnectionService.getAginAuthorization(request).pipe(
          map(response => aginActions.getAginAuthorizationSuccess({ response })),
          catchError(error => {
            this.alertService.showError(
              'Connection setup failed',
              'Please try again later',
              ToasterType.snackbarError,
              {
                positionClass: ToasterPosition.bottomCenter,
                showToastAction: false,
                type: ToasterType.snackbarError,
                toastClass: ToasterClass.snackbarError,
                closeButton: true,
                disableTimeOut: false,
              }
            );
            return of(aginActions.getAginAuthorizationFailure({ error }));
          })
        )
      )
    )
  );

  exchangeCodeForToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(aginActions.exchangeAginCodeForToken),
      mergeMap(({ platformId, code, state }) =>
        this.aginConnectionService.exchangeAginCodeForToken(platformId, code, state).pipe(
          map(() => {
            this.alertService.callSnackbarSuccess(
              'Connection established successfully',
              '',
              ToasterType.snackbarSuccess,
              {
                positionClass: ToasterPosition.bottomCenter,
                showToastAction: false,
                type: ToasterType.snackbarSuccess,
                toastClass: ToasterClass.snackbarSuccess,
                closeButton: true,
                disableTimeOut: false,
              }
            );
            return aginActions.exchangeAginCodeForTokenSuccess();
          }),
          catchError(error => {
            this.alertService.showError(
              'Failed to complete connection setup',
              'Please try again',
              ToasterType.snackbarError,
              {
                positionClass: ToasterPosition.bottomCenter,
                showToastAction: false,
                type: ToasterType.snackbarError,
                toastClass: ToasterClass.snackbarError,
                closeButton: true,
                disableTimeOut: false,
              }
            );
            return of(aginActions.exchangeAginCodeForTokenFailure({ error }));
          })
        )
      )
    )
  );

  loadConnectionWithAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(aginActions.loadAginConnectionWithAssets),
      mergeMap(({ connectionId }) =>
        this.aginConnectionService.getAginConnectionWithAssets(connectionId).pipe(
          map(connection => aginActions.loadAginConnectionWithAssetsSuccess({ connection })),
          catchError(error => {
            this.alertService.showError(
              this.translateService.instant('couplings.notifications.load_connection.failure'),
              this.translateService.instant('alert_message.try_again'),
              ToasterType.snackbarError,
              {
                positionClass: ToasterPosition.bottomCenter,
                showToastAction: false,
                type: ToasterType.snackbarError,
                toastClass: ToasterClass.snackbarError,
                closeButton: true,
                disableTimeOut: false,
              }
            );
            return of(aginActions.loadAginConnectionWithAssetsFailure({ error, connectionId }));
          })
        )
      )
    )
  );

  refreshConnectionsAfterCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(aginActions.createAginConnectionSuccess),
      mergeMap(() => [
        aginActions.loadAginConnections({ page: 0, size: 20 }),
        aginActions.loadAginPlatformsWithConnections(),
      ])
    )
  );

  triggerAuthorizationAfterCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(aginActions.createAginConnectionSuccess),
      withLatestFrom(
        this.store.select(selectAginPendingConnectionData),
        this.store.select(selectAginPlatformsWithConnections)
      ),
      mergeMap(([{ connectionRequestId }, pendingData]) => {
        if (!pendingData) {
          return [];
        }

        const scopesString = pendingData.scopesGranted.join('+');

        const authorizationRequest: IAginAuthorizationRequest = {
          scope: scopesString,
          connectionRequestId,
          platformRespondingId: pendingData.platformId,
        };

        return [
          aginActions.getAginAuthorization({ request: authorizationRequest }),
          aginActions.clearPendingConnectionData(),
        ];
      })
    )
  );

  handleAuthorizationRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(aginActions.getAginAuthorizationSuccess),
        tap(({ response }) => {
          if (response.redirectUri) {
            window.location.href = response.redirectUri;
          }
        })
      ),
    { dispatch: false }
  );

  refreshConnectionsAfterDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(aginActions.deleteAginConnectionSuccess),
      mergeMap(() => [
        aginActions.loadAginConnections({ page: 0, size: 20 }),
        aginActions.loadAginPlatformsWithConnections(),
      ])
    )
  );

  oauthCompletionToast$ = createEffect(() => this.actions$.pipe(ofType(aginActions.exchangeAginCodeForTokenSuccess)), {
    dispatch: false,
  });

  authorizationRedirectToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(aginActions.getAginAuthorizationSuccess),
        tap(({ response }) => {
          if (response.redirectUri) {
            this.alertService.showInfo(
              'Redirecting to authorization page...',
              'Authorization',
              ToasterType.snackbarSuccess,
              {
                positionClass: ToasterPosition.bottomCenter,
                showToastAction: false,
                type: ToasterType.snackbarSuccess,
                toastClass: ToasterClass.snackbarSuccess,
                closeButton: true,
                disableTimeOut: false,
              }
            );
          }
        })
      ),
    { dispatch: false }
  );

  createSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(aginActions.createAginSubscription),
      mergeMap(({ connectionId, subscriptionRequest }) =>
        this.aginConnectionService.createSubscription(connectionId, subscriptionRequest).pipe(
          map(response =>
            aginActions.createAginSubscriptionSuccess({
              connectionId,
              assetId: subscriptionRequest.assets[0].assetId,
              subscriptionId: response.subscriptionId,
            })
          ),
          catchError(error => {
            this.alertService.showError('Failed to subscribe to asset', 'Please try again', ToasterType.snackbarError, {
              positionClass: ToasterPosition.bottomCenter,
              showToastAction: false,
              type: ToasterType.snackbarError,
              toastClass: ToasterClass.snackbarError,
              closeButton: true,
              disableTimeOut: false,
            });
            return of(
              aginActions.createAginSubscriptionFailure({
                error,
                connectionId,
                assetId: subscriptionRequest.assets[0].assetId,
              })
            );
          })
        )
      )
    )
  );

  deleteSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(aginActions.deleteAginSubscription),
      mergeMap(({ connectionId, subscriptionId, assetId }) =>
        this.aginConnectionService.deleteSubscription(connectionId, subscriptionId, assetId).pipe(
          map(() => aginActions.deleteAginSubscriptionSuccess({ connectionId, assetId })),
          catchError(error => {
            this.alertService.showError(
              'Failed to unsubscribe from asset',
              'Please try again',
              ToasterType.snackbarError,
              {
                positionClass: ToasterPosition.bottomCenter,
                showToastAction: false,
                type: ToasterType.snackbarError,
                toastClass: ToasterClass.snackbarError,
                closeButton: true,
                disableTimeOut: false,
              }
            );
            return of(aginActions.deleteAginSubscriptionFailure({ error, connectionId, assetId }));
          })
        )
      )
    )
  );

  createSubscriptionSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(aginActions.createAginSubscriptionSuccess),
        tap(() => {
          this.alertService.showSuccess('Successfully subscribed to asset', '', ToasterType.snackbarSuccess, {
            positionClass: ToasterPosition.bottomCenter,
            showToastAction: false,
            type: ToasterType.snackbarSuccess,
            toastClass: ToasterClass.snackbarSuccess,
            closeButton: true,
            disableTimeOut: false,
          });
        })
      ),
    { dispatch: false }
  );

  deleteSubscriptionSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(aginActions.deleteAginSubscriptionSuccess),
        tap(() => {
          this.alertService.showSuccess('Successfully unsubscribed from asset', '', ToasterType.snackbarSuccess, {
            positionClass: ToasterPosition.bottomCenter,
            showToastAction: false,
            type: ToasterType.snackbarSuccess,
            toastClass: ToasterClass.snackbarSuccess,
            closeButton: true,
            disableTimeOut: false,
          });
        })
      ),
    { dispatch: false }
  );
}
